/**
 * Unit Renderer - uses InstancedMesh for efficient rendering of many units
 * Redesigned with Chinese ancient style Low-Poly Q-style characters
 * Features: Large heads, Chinese helmets/armor, faction color support
 */

import * as THREE from 'three';
import type { FactionId, UnitType, Entity } from '@/types';
import { MAX_UNITS_PER_TYPE, FACTION_COLORS } from './unitConstants';
import { factionVertexShader, factionFragmentShader } from './unitShaders';
import type { UnitRenderData } from './unitTypes';
import {
  createSwordsmanGeometry,
  createArcherGeometry,
  createSpearmanGeometry,
  createCavalryGeometry,
  createShieldBearerGeometry,
} from './geometries';

export class UnitRenderer {
  private scene: THREE.Scene;
  private instancedMeshes: Map<UnitType, THREE.InstancedMesh> = new Map();
  private unitData: Map<UnitType, UnitRenderData[]> = new Map();
  private materials: Map<FactionId, THREE.ShaderMaterial> = new Map();

  private dummy = new THREE.Object3D();

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    this.createMaterials();
    this.createMeshes();
  }

  private createMaterials(): void {
    // Create a material for each faction
    for (const [factionId, colors] of Object.entries(FACTION_COLORS)) {
      const material = new THREE.ShaderMaterial({
        vertexShader: factionVertexShader,
        fragmentShader: factionFragmentShader,
        uniforms: {
          primaryColor: { value: colors.primary },
          secondaryColor: { value: colors.secondary },
          ambientLightColor: { value: new THREE.Color(0xffffff) },
          directionalLightColor: { value: new THREE.Color(0xffffff) },
          directionalLightDirection: {
            value: new THREE.Vector3(0.5, 1.0, 0.3).normalize(),
          },
        },
      });
      this.materials.set(factionId as FactionId, material);
    }
  }

  private createMeshes(): void {
    // Note: 'cavalry' is handled by CavalryRenderer for animated rendering
    // Note: 'shield' is handled by ShieldBearerRenderer for animated rendering
    // Note: 'taoist' is handled by MageRenderer for animated rendering
    // Note: 'archer' is handled by ArcherRenderer for animated rendering
    // Note: 'sword' is handled by SwordsmanRenderer for animated rendering
    // Note: 'spear' is handled by SpearmanRenderer for animated rendering
    const unitTypes: UnitType[] = [
      // All unit types now have specialized animated renderers
    ];

    for (const unitType of unitTypes) {
      const geometry = this.createUnitGeometry(unitType);

      // Use standard material with vertex colors
      // Faction colors will be baked into geometry when needed
      const material = new THREE.MeshStandardMaterial({
        vertexColors: true,
        roughness: 0.7,
        metalness: 0.2,
      });

      const mesh = new THREE.InstancedMesh(
        geometry,
        material,
        MAX_UNITS_PER_TYPE
      );
      mesh.count = 0;
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      mesh.frustumCulled = false;

      this.instancedMeshes.set(unitType, mesh);
      this.unitData.set(unitType, []);
      this.scene.add(mesh);
    }
  }

  private createUnitGeometry(unitType: UnitType): THREE.BufferGeometry {
    switch (unitType) {
      case 'sword':
        return createSwordsmanGeometry();
      case 'archer':
        return createArcherGeometry();
      case 'spear':
        return createSpearmanGeometry();
      case 'cavalry':
        return createCavalryGeometry();
      case 'shield':
        return createShieldBearerGeometry();
      default:
        return createSwordsmanGeometry();
    }
  }

  addUnit(
    entity: Entity,
    unitType: UnitType,
    x: number,
    y: number,
    z: number,
    factionId: FactionId
  ): void {
    const data = this.unitData.get(unitType);
    if (!data) return;

    data.push({
      entity,
      x,
      y,
      z,
      factionId,
      scale: 1.75, // Scaled down to half size
      rotation: 0,
    });
  }

  removeUnit(entity: Entity, unitType: UnitType): void {
    const data = this.unitData.get(unitType);
    if (!data) return;

    const index = data.findIndex((d) => d.entity === entity);
    if (index !== -1) {
      data.splice(index, 1);
    }
  }

  updateUnit(
    entity: Entity,
    unitType: UnitType,
    x: number,
    y: number,
    z: number,
    rotation?: number
  ): void {
    const data = this.unitData.get(unitType);
    if (!data) return;

    const unit = data.find((d) => d.entity === entity);
    if (unit) {
      // Calculate rotation from movement direction
      if (rotation !== undefined) {
        unit.rotation = rotation;
      } else if (unit.x !== x || unit.z !== z) {
        unit.rotation = Math.atan2(x - unit.x, z - unit.z);
      }
      unit.x = x;
      unit.y = y;
      unit.z = z;
    }
  }

  update(): void {
    for (const [unitType, mesh] of this.instancedMeshes) {
      const data = this.unitData.get(unitType)!;

      mesh.count = data.length;

      for (let i = 0; i < data.length; i++) {
        const unit = data[i];

        // Update transform with rotation
        // Small offset to prevent z-fighting with terrain (feet are at ~y=0.01 in model)
        const surfaceOffset = 0.02;
        this.dummy.position.set(unit.x, unit.y + surfaceOffset, unit.z);
        this.dummy.rotation.set(0, unit.rotation, 0);
        this.dummy.scale.setScalar(unit.scale);
        this.dummy.updateMatrix();
        mesh.setMatrixAt(i, this.dummy.matrix);
      }

      mesh.instanceMatrix.needsUpdate = true;
    }
  }

  getUnitCount(): number {
    let count = 0;
    for (const data of this.unitData.values()) {
      count += data.length;
    }
    return count;
  }

  clear(): void {
    for (const data of this.unitData.values()) {
      data.length = 0;
    }
    for (const mesh of this.instancedMeshes.values()) {
      mesh.count = 0;
    }
  }

  dispose(): void {
    for (const mesh of this.instancedMeshes.values()) {
      mesh.geometry.dispose();
      if (mesh.material instanceof THREE.Material) {
        mesh.material.dispose();
      }
      this.scene.remove(mesh);
    }
    this.instancedMeshes.clear();
    this.unitData.clear();

    // Dispose faction materials
    for (const material of this.materials.values()) {
      material.dispose();
    }
    this.materials.clear();
  }
}
